import React, { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/Badge/Badge';
import { Button } from '@/components/ui/Button/Button';
import { Avatar } from '@/components/ui/Avatar/Avatar';
import { Table, type TableFilter } from '@/components/ui/Table';
import { TableActionButton, TableActions } from '@/components/ui';
import { Modal } from '@/components/ui/Modal/Modal';
import { Input } from '@/components/ui/Input/Input';
import { MultiSelect } from '@/components/ui/MultiSelect/MultiSelect';
import { PermissionGate } from '@/components/ui/PermissionGate';
import { PERMISSIONS } from '@/constants/permissions';
import { EditMemberSheet } from '@/components/organization/EditMemberSheet';
// EditMemberRolesModal is kept (file untouched) but no longer imported here.
// It is superseded by the EditMemberSheet for this panel (AC-Users-3).
import {
  useActiveOrgId,
  useOrgMembers,
  useOrgInvites,
  useInviteMemberMutation,
  useRevokeInviteMutation,
} from '@/hooks/queries';
// Note: useRemoveMemberMutation + useUpdateMemberRoleMutation are now used
// inside EditMemberSheet; they are not needed directly in this panel.
import { useQuery } from '@tanstack/react-query';
import { RolesService } from '@/services/RolesService';
import { OrgService } from '@/services/OrgService';
import { showToast } from '@/components/ui/Toast';
import { formatDate, formatTime } from '@/lib/localeFormatters';
import { t } from '@/lib/i18n';
import { Edit, Send, X, Plus } from 'lucide-react';

/**
 * Unified row shape — members and pending invites in one table.
 *
 * AC-Users-2: `kind` field is retained in the data model (no breaking API
 * change) but is no longer rendered as a column or used as a filter pill.
 */
interface UnifiedRow {
  kind: 'member' | 'invite';
  id: string;
  email: string;
  name?: string;
  // Member-only
  userId?: string;
  roleLabel?: string;      // owner/admin/member
  joinedAt?: string;
  roles?: Array<{ id: string; name: string; is_system: boolean }>;
  avatarUrl?: string | null;
  isActive?: boolean;      // AC-Users-4: member active state
  // Invite-only
  inviteStatus?: 'pending' | 'accepted' | 'expired';
  sentAt?: string;
  expiresAt?: string;
  inviteRole?: string;
  // Normalized status for the Status filter pill
  // AC-Users-2: invite rows use 'invited' instead of 'pending'
  status: 'active' | 'inactive' | 'invited' | 'accepted' | 'expired';
}

/**
 * MembersAndInvitesPanel — unified members + invites list.
 *
 * Polish Wave 6 P2 changes (AC-Users-1 through AC-Users-5):
 *   - Button label: "Invite User" (was "Invite Member")
 *   - Type column + Type filter removed; Status filter retained
 *   - Invite rows show status badge "Invited" (not "Pending")
 *   - Member row actions collapsed to single "Edit" button → EditMemberSheet
 *   - Inactive members visible with "Inactive" badge (AC-Users-4)
 *   - Tab label change is in SystemSettingsPage (this file exports the panel)
 */
export function MembersAndInvitesPanel() {
  const orgId = useActiveOrgId();

  const { data: rawMembers = [], isLoading: membersLoading } = useOrgMembers(orgId || undefined);
  const { data: rawInvites = [], isLoading: invitesLoading } = useOrgInvites(orgId || undefined);
  const inviteMutation = useInviteMemberMutation();
  const revokeInviteMutation = useRevokeInviteMutation();

  const { data: availableRoles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['roles', orgId],
    queryFn: async () => {
      const rolesService = new RolesService();
      return rolesService.getRoles();
    },
    enabled: !!orgId,
  });

  // ─── Invite modal state ─────────────────────────────────────────
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [email, setEmail] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [inviteResult, setInviteResult] = useState<{ inviteLink?: string; emailSent?: boolean } | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // ─── Edit member sheet state (AC-Users-3) ──────────────────────
  const [editSheet, setEditSheet] = useState<{
    isOpen: boolean;
    userId: string | null;
    userEmail: string;
    userName?: string;
    avatarUrl?: string | null;
    joinedAt?: string;
    isActive: boolean;
    roleLabel?: string;
  }>({ isOpen: false, userId: null, userEmail: '', isActive: true });

  // ─── Normalize members + invites into one array ────────────────
  const rows: UnifiedRow[] = useMemo(() => {
    const memberRows: UnifiedRow[] = (rawMembers || []).map((m: any) => ({
      kind: 'member',
      id: `member-${m.user_id}`,
      userId: m.user_id,
      email: m.email,
      name: m.full_name,
      roleLabel: m.role,
      joinedAt: m.joined_at,
      roles: m.roles || [],
      avatarUrl: m.avatar_url || null,
      isActive: m.is_active !== false, // default true for backward compat
      // AC-Users-4: show 'inactive' badge for suspended members
      status: m.is_active === false ? 'inactive' : 'active',
    }));
    const inviteRows: UnifiedRow[] = (rawInvites || []).map((i: any) => {
      const expired = new Date(i.expires_at) < new Date();
      const baseStatus: 'accepted' | 'expired' | 'invited' = i.accepted_at
        ? 'accepted'
        : expired
        ? 'expired'
        : 'invited'; // AC-Users-2: use 'invited' instead of 'pending'
      return {
        kind: 'invite',
        id: `invite-${i.id}`,
        email: i.email,
        inviteStatus: baseStatus === 'invited' ? 'pending' : baseStatus,
        sentAt: i.created_at,
        expiresAt: i.expires_at,
        inviteRole: i.role,
        status: baseStatus,
      };
    });
    // B13 belt-and-braces: filter out any invite whose email matches an existing member.
    const memberEmailSet = new Set(memberRows.map((m) => (m.email || '').toLowerCase()));
    const filteredInviteRows = inviteRows.filter((i) => !memberEmailSet.has((i.email || '').toLowerCase()));
    return [...memberRows, ...filteredInviteRows];
  }, [rawMembers, rawInvites]);

  // ─── Filter pills — client-side via Table ─────────────────────
  // AC-Users-2: Type filter removed. Status filter retained.
  const filters: TableFilter[] = [
    {
      key: 'status',
      label: t('Status'),
      options: [
        { label: t('Active'), value: 'active' },
        { label: t('Inactive'), value: 'inactive' },
        { label: t('Invited'), value: 'invited' },
        { label: t('Accepted'), value: 'accepted' },
        { label: t('Expired'), value: 'expired' },
      ],
    },
  ];

  // ─── Mutation handlers ─────────────────────────────────────────
  const handleInvite = async () => {
    if (!orgId || !email.trim() || selectedRoleIds.length === 0) return;
    try {
      const result = await inviteMutation.mutateAsync({
        orgId,
        email: email.trim().toLowerCase(),
        roleIds: selectedRoleIds,
      });
      setEmail('');
      setSelectedRoleIds([]);
      setInviteError(null);
      setInviteResult({ inviteLink: result?.inviteLink, emailSent: result?.emailSent });
    } catch (e) {
      setInviteError(e instanceof Error ? e.message : 'Failed to invite');
    }
  };

  const handleResendInvite = async (inviteId: string) => {
    if (!orgId) return;
    try {
      const svc = new OrgService();
      await svc.resendInvite(orgId, inviteId);
      showToast.success(t('Invite resent successfully'));
    } catch (e) {
      showToast.error(e instanceof Error ? e.message : t('Failed to resend invite'));
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    if (!orgId) return;
    try {
      await revokeInviteMutation.mutateAsync({ orgId, inviteId });
    } catch (e) {
      showToast.error(e instanceof Error ? e.message : t('Failed to revoke invite'));
    }
  };

  // ─── Table columns ─────────────────────────────────────────────
  // AC-Users-2: Type column REMOVED. Columns: User, Roles, Status, Joined/Sent, Actions.
  const columns = [
    {
      key: 'user',
      title: t('User'),
      sortable: true,
      sortKey: (r: UnifiedRow) => (r.name || r.email || '').toLowerCase(),
      render: (_: any, record: UnifiedRow) => {
        const isInvite = record.kind === 'invite';
        return (
          <div className="flex items-center gap-3">
            {isInvite ? (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <Send className="w-4 h-4" />
              </div>
            ) : (
              <Avatar
                size="sm"
                src={record.avatarUrl || undefined}
                initials={record.name ? record.name.charAt(0).toUpperCase() : record.email.charAt(0).toUpperCase()}
                name={record.name || record.email}
              />
            )}
            <div>
              <div className={`font-medium ${isInvite ? 'text-muted-foreground italic' : 'text-foreground'}`}>
                {record.name || (isInvite ? t('Invited user') : record.email)}
              </div>
              <div className="text-sm text-muted-foreground">{record.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'roles',
      title: t('Roles'),
      sortable: true,
      sortKey: (r: UnifiedRow) =>
        r.kind === 'member'
          ? (r.roleLabel || r.roles?.[0]?.name || '').toLowerCase()
          : (r.inviteRole || '').toLowerCase(),
      render: (_: any, record: UnifiedRow) => {
        if (record.kind === 'invite') {
          return record.inviteRole ? (
            <Badge variant="outline" className="text-xs italic">
              {record.inviteRole}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          );
        }
        if (record.roles && record.roles.length > 0) {
          return (
            <div className="flex flex-wrap gap-1">
              {record.roles.map((role) => (
                <Badge key={role.id} variant={role.is_system ? 'primary' : 'secondary'}>
                  {role.name}
                </Badge>
              ))}
            </div>
          );
        }
        return <span className="text-sm text-muted-foreground">{t('No roles assigned')}</span>;
      },
    },
    {
      key: 'status',
      title: t('Status'),
      sortable: true,
      render: (value: string) => {
        switch (value) {
          case 'active':
            return <Badge variant="success">{t('Active')}</Badge>;
          case 'inactive':
            return <Badge variant="warning">{t('Inactive')}</Badge>;
          // AC-Users-2: invite rows now render "Invited" (not "Pending")
          case 'invited':
            return <Badge variant="outline">{t('Invited')}</Badge>;
          case 'accepted':
            return <Badge variant="success">{t('Accepted')}</Badge>;
          case 'expired':
            return <Badge variant="error">{t('Expired')}</Badge>;
          default:
            return <Badge variant="outline">{value}</Badge>;
        }
      },
    },
    {
      key: 'joinedAt',
      title: t('Joined / Sent'),
      sortable: true,
      render: (_: any, record: UnifiedRow) => {
        const raw = record.kind === 'member' ? record.joinedAt : record.sentAt;
        if (!raw) return <span className="text-muted-foreground">—</span>;
        const date = new Date(raw);
        return (
          <div>
            <div className="text-sm">{formatDate(date)}</div>
            <div className="text-xs text-muted-foreground">{formatTime(date)}</div>
          </div>
        );
      },
    },
    {
      key: 'actions',
      title: t('Actions'),
      align: 'right' as const,
      render: (_: any, record: UnifiedRow) => {
        if (record.kind === 'invite') {
          return (
            <TableActions>
              <TableActionButton
                icon={Send}
                onClick={() => handleResendInvite(record.id.replace(/^invite-/, ''))}
                disabled={record.status !== 'invited'}
              >
                {t('Resend')}
              </TableActionButton>
              <TableActionButton
                variant="destructive"
                icon={X}
                onClick={() => handleRevokeInvite(record.id.replace(/^invite-/, ''))}
              >
                {t('Cancel')}
              </TableActionButton>
            </TableActions>
          );
        }
        // AC-Users-3: Single "Edit" button for member rows (replaces Edit Roles + Set Password + Remove trio)
        return (
          <PermissionGate anyPermissions={[PERMISSIONS.MANAGE_USERS, PERMISSIONS.EDIT_USER_ROLES]} fallback={null}>
            <TableActions>
              <TableActionButton
                icon={Edit}
                onClick={() =>
                  setEditSheet({
                    isOpen: true,
                    userId: record.userId!,
                    userEmail: record.email,
                    userName: record.name,
                    avatarUrl: record.avatarUrl,
                    joinedAt: record.joinedAt,
                    isActive: record.isActive !== false,
                    roleLabel: record.roleLabel,
                  })
                }
                data-testid={`edit-member-btn-${record.userId}`}
              >
                {t('Edit')}
              </TableActionButton>
            </TableActions>
          </PermissionGate>
        );
      },
    },
  ];

  if (!orgId) {
    return <div className="text-sm text-muted-foreground">{t('No organization selected.')}</div>;
  }

  const loading = membersLoading || invitesLoading;

  return (
    <div>
      {/* ─── Invite User button — AC-Users-1: was "Invite Member" ─── */}
      <PermissionGate anyPermissions={[PERMISSIONS.MANAGE_USERS, PERMISSIONS.INVITE_USERS]}>
        <div className="flex items-center justify-end mb-4">
          <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowInviteModal(true)}>
            {t('Invite User')}
          </Button>
        </div>
      </PermissionGate>

      <Table
        data={rows}
        columns={columns}
        loading={loading}
        filters={filters}
        searchPlaceholder={t('Search by email or name...')}
        emptyText={t('No members or invites found.')}
      />

      {/* Invite Modal */}
      {showInviteModal && (
        <Modal
          isOpen={showInviteModal}
          onClose={() => {
            setShowInviteModal(false);
            setInviteResult(null);
            setInviteError(null);
          }}
          title={inviteResult ? t('Invitation Created') : t('Invite Team Member')}
          size="md"
        >
          {inviteResult ? (
            <div className="space-y-4">
              {inviteResult.emailSent ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700">
                  {t('Invitation email sent. They have 7 days to accept.')}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
                  {t('Email not configured — share the invite link manually.')}
                </div>
              )}
              {inviteResult.inviteLink && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">{t('Invite Link')}</label>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={inviteResult.inviteLink}
                      className="flex-1 font-mono text-muted-foreground"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigator.clipboard.writeText(inviteResult.inviteLink!)}
                    >
                      {t('Copy')}
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex justify-end pt-2">
                <Button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteResult(null);
                  }}
                >
                  {t('Done')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Input
                label={t('Email Address')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('Enter email address')}
                required
              />
              <MultiSelect
                label={t('Roles')}
                placeholder={t('Select one or more roles...')}
                value={selectedRoleIds}
                onChange={setSelectedRoleIds}
                options={availableRoles.map((role) => ({
                  value: role.id,
                  label: role.name,
                  description: role.description || undefined,
                }))}
                required
              />
              {inviteError && <p className="text-sm text-error-600">{inviteError}</p>}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleInvite}
                  disabled={!email.trim() || selectedRoleIds.length === 0 || inviteMutation.isPending || rolesLoading}
                  isLoading={inviteMutation.isPending}
                  className="flex-1"
                >
                  {t('Send Invitation')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviteMutation.isPending}
                >
                  {t('Cancel')}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* AC-Users-3: Edit Member Sheet (replaces the 3-button action set for member rows) */}
      {editSheet.isOpen && editSheet.userId && orgId && (
        <EditMemberSheet
          isOpen={editSheet.isOpen}
          onClose={() =>
            setEditSheet({ isOpen: false, userId: null, userEmail: '', isActive: true })
          }
          orgId={orgId}
          userId={editSheet.userId}
          userEmail={editSheet.userEmail}
          userName={editSheet.userName}
          avatarUrl={editSheet.avatarUrl}
          joinedAt={editSheet.joinedAt}
          isActive={editSheet.isActive}
          roleLabel={editSheet.roleLabel}
        />
      )}
    </div>
  );
}
